import React, { useEffect, useState } from 'react';
import { Table, Tag, Typography, Input, Button, DatePicker, Tabs, Space } from 'antd';
import SearchFieldsSelect, { useSearchFields, getSearchFieldsPlaceholder } from '../../Components/SearchFieldsSelect/SearchFieldsSelect';
import dayjs from 'dayjs';
import API from '../../API/API';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const leadStageColors = {
  'New': { color: '#1890ff', background: '#e6f7ff' },
  'Call Back': { color: '#faad14', background: '#fffbe6' },
  'Voice Mail': { color: '#9254de', background: '#f9f0ff' },
  'Follow-Up': { color: '#fa8c16', background: '#fff7e6' },
  'Quoted': { color: '#13c2c2', background: '#e6fffb' },
  'Negotiation': { color: '#eb2f96', background: '#fff0f6' },
  'Pending Payment': { color: '#fa541c', background: '#fff2e8' },
  'Payment Completed': { color: '#52c41a', background: '#f6ffed' },
  'Sales Made': { color: '#389e0d', background: '#f6ffed' },
  'Part Not Available': { color: '#bfbfbf', background: '#fafafa' },
  'Wrong Number': { color: '#ff4d4f', background: '#fff1f0' },
  'Not Interested': { color: '#d46b08', background: '#fff7e6' },
  'Hang Up': { color: '#8c8c8c', background: '#f5f5f5' },
  'Fake': { color: '#a8071a', background: '#fff1f0' },
  'Test Lead': { color: '#7cb305', background: '#fcffe6' },
};

const AllLeadsView = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // The search a request uses: the box's text, set 0.5 s after typing pauses
  // (at once when the box is cleared), so a search is not sent per keystroke.
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      setSearchQuery('');
      return undefined;
    }
    const timer = setTimeout(() => setSearchQuery(trimmed), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);
  const [searchFields, setSearchFields] = useSearchFields('all-leads-view:search-fields');
  // The "Search in" group only changes the results of a search, so changing it
  // with an empty search box sends no request.
  const effectiveSearchFields = searchQuery ? searchFields : null;
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [activeTab, setActiveTab] = useState("my"); // "my" or "team"
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const agentId = userData.id;
  const subRole = userData.subRole; // assuming it's stored as 'subrole'

  const fetchLeads = async () => {
    if (!agentId) {
      console.error("User ID not found in localStorage");
      return;
    }

    setLoading(true);
    try {
      const params = {
        dateField: 'createdAt',
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        page: currentPage,
        limit: pageSize,
      };

      if (searchQuery) {
        params.search = searchQuery;
        params.searchFields = searchFields;
      }

      // Decide which param to send based on active tab
      if (activeTab === "my") {
        params.agentId = agentId;
        params.agentMode = "include";
      } else if (activeTab === "team") {
        params.agentId = agentId;
        params.agentMode = "exclude";
      }

      if (subRole ) {
        params.tierType = subRole; 
      }
      // The server sends only the fields this page uses
      // (LeadController LEAD_VIEW_FIELDS['all-leads-view']).
      params.view = 'all-leads-view';
      const res = await API.leadGetAll(params);
      if (res?.status) {
        setLeads(res.data);
        setTotalRecords(res.pagination.totalRecords);
      }
    } catch (err) {
      console.error('Failed to fetch leads', err);
    } finally {
      setLoading(false);
    }
  };

  // Refetch whenever any filter changes
  useEffect(() => {
    fetchLeads();
  }, [searchQuery, effectiveSearchFields, currentPage, pageSize, dateRange, activeTab]);

  const formatToPST = (isoDateString) => {
    const date = new Date(isoDateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const columns = [
    { title: "Sales-Agent", dataIndex: "agentName", key: "agentName" },
    { title: "Year", dataIndex: ["vehicleInfo", "year"], key: "year" },
    {
      title: "Make/Model",
      key: "make",
      render: (_, record) => (
        <>
          <strong>{record.vehicleInfo.make}</strong> <br />
          <span>{record.vehicleInfo.model}</span><br />
          <span>{record.vehicleInfo.vin}</span>
        </>
      )
    },
    { title: "Part", dataIndex: ["vehicleInfo", "part"], key: "part" },
    {
      title: "Customer",
      key: "customer",
      render: (_, record) => (
        <>
          <strong>{record.personalInfo.fullName}</strong><br />
          <span>{record.personalInfo.email}</span><br />
          <span>{record.personalInfo.phoneNumber}</span>
        </>
      )
    },
    {
      title: "Status",
      dataIndex: "leadStage",
      key: "leadStage",
      render: (stage) => {
        const styleConfig = leadStageColors[stage] || {};
        return (
          <Tag
            style={{
              color: styleConfig.color,
              backgroundColor: styleConfig.background,
              borderRadius: '8px',
              padding: '0 10px',
              fontWeight: 500,
            }}
          >
            {stage}
          </Tag>
        );
      }
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatToPST(date)
    }
  ];

  const tabItems = [
    {
      key: "my",
      label: "My Leads",
    },
    {
      key: "team",
      label: "Team Leads",
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            type="link"
            onClick={() => navigate("/my-leads")}
            style={{ fontSize: '16px', padding: 0 }}
          >
            <ArrowLeftOutlined /> Back /
          </Button>
          <Title level={3} style={{ margin: '0 0 0 12px' }}>
            {activeTab === "my" ? "My Leads" : "Team Leads"}
          </Title>
        </div>

        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <Space.Compact>
            <SearchFieldsSelect value={searchFields} onChange={setSearchFields} size="middle" />
            <Input
              placeholder={getSearchFieldsPlaceholder(searchFields)}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 260 }}
              allowClear
            />
          </Space.Compact>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            disabledDate={(current) => current && current > dayjs().endOf('day')}
            style={{ minWidth: 250 }}
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        style={{ marginBottom: 16 }}
      />

      {/* Table */}
      <Table
        columns={columns}
        dataSource={leads}
        rowKey={(record) => record._id}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalRecords,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} leads`,
        }}
        onChange={(pagination) => {
          setCurrentPage(pagination.current);
          setPageSize(pagination.pageSize);
        }}
        scroll={{
          x: 'max-content',
          y: '55vh'
        }}
      />
    </div>
  );
};

export default AllLeadsView;